import React, { useEffect, useMemo, useState } from 'react';
import DefaultModal from 'components/Utils/DefaultModal';
import DefaultButton from 'components/Utils/NewDefaultButton';
import TagInput from 'components/Utils/TagInput';
import { DrugInputView } from '../NewPrescription/view';
import './style.scss';
import { api } from '../../../../careand-redux/api/ApiThunks';
import { useAppDispatch } from '../../../../careand-redux/useAppDispatch';
import { FavoritePrescriptionMedication, PrescriptionMedication } from '../../../../generated/api';
import { useAppSelector } from '../../../../careand-redux/store';

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
  medication?: PrescriptionMedication;
  existingFavorite?: FavoritePrescriptionMedication;
};

export const AddToFavoritesModal: React.FC<Props> = ({
  isOpen,
  onRequestClose,
  medication,
  existingFavorite,
}) => {
  const dispatch = useAppDispatch();
  const favorites = useAppSelector((state) => state.drugs.favoriteMedications);
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [editedMedication, setEditedMedication] = useState<PrescriptionMedication | undefined>(
    medication,
  );
  const [drugbankKey, setDrugbankKey] = useState(0);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setEditedMedication(medication);
      setDrugbankKey((prev) => prev + 1);
      setTags([]);
    }
  }, [isOpen, medication]);

  const allTags = useMemo(() => {
    const result: string[] = [];
    favorites.forEach((fav) => {
      (fav.tags || []).forEach((tag) => result.push(tag));
    });
    return result;
  }, [favorites]);

  const handleUpdateMedication = (value: PrescriptionMedication) => {
    setEditedMedication(value);
  };

  async function handleSaveFavorite() {
    setLoading(true);
    const options = { errorTitle: 'Favorite Medication' };
    try {
      await dispatch(
        api.Drugs.addFavoriteMedication({
          favoritePrescriptionMedication: {
            value: editedMedication,
            tags,
          },
        }),
        options,
      );
    } finally {
      setLoading(false);
      onRequestClose();
    }
  }

  return (
    <DefaultModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="add-to-favorites-modal fit-modal"
      shouldCloseOnEsc={true}
      title={existingFavorite ? 'Edit Favorite Medication' : 'Add to Favorites'}
      wrap={true}
      padding={true}
      content={
        <div className="modal-content">
          <div className="medication-form">
            <DrugInputView
              medication={editedMedication}
              drugbankKey={drugbankKey}
              onFormChange={handleUpdateMedication}
              hideFavorites={true}
            />
          </div>

          <div className="tags-section">
            <label className="small-title">Tags (optional):</label>
            <p className="helper-text">
              Add tags to organize this medication. Separate multiple tags with commas.
            </p>
            <TagInput onChange={setTags} suggestions={allTags} />
          </div>

          <div className="button-row">
            <DefaultButton
              name="Cancel"
              onClick={onRequestClose}
              className="white"
              disabled={loading}
            />
            <DefaultButton
              name={existingFavorite ? 'Save Changes' : 'Add to Favorites'}
              onClick={handleSaveFavorite}
              loading={loading}
              className="blue"
            />
          </div>
        </div>
      }
    />
  );
};

export default AddToFavoritesModal;
